import React, { useEffect, useState } from "react";
import { Box, Typography, Button, CardMedia, Avatar } from "@mui/material";
import { Purchase } from "types";
import { useAuth } from "context/authContext";
import dayjs from "dayjs";

interface LearningItem {
  coverPhotoUrl?: string;
  title?: string;
  category?: string;
  author?: string;
  type?: string;
  progress?: number;
  lastAccessed?: string;
  contentData?: any;
  cartItemData?: any;
}

interface LearningCardProps {
  item: LearningItem & { id: string } & Purchase;
  showProgressBar?: boolean;
  showLastAccessed?: boolean;
  progress?: number;
  onStartClick: (cartItem: any) => void; // Accepts a cartItem parameter
  onContinueClick: (cartItem: any) => void; // Accepts a cartItem parameter
  onDownloadClick: (cartItem: any) => void;
}

const LearningCard: React.FC<LearningCardProps> = ({
  item,
  showProgressBar = true,
  showLastAccessed = true,
  progress = 0, // ✅ Get the progress from props
  onStartClick,
  onContinueClick,
  onDownloadClick,
}) => {

  const { currentUser } = useAuth();
  // Extracting and setting myLearningData directly from item.cartItemData
  const myLearningData = item.cartItemData || [];

  // Correct usage of map
  myLearningData.map((item: any, index: number) => {

    return item?.contentData?.contentStructure?.length;
  });

  // const totalContent = item?.contentData?.contentStructure?.length || 1; // Avoid division by zero
  // const completedContent = item?.contentData?.contentStructure?.length;
  // const progress2 = (completedContent / totalContent) * 100;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={1}
      border={1}
      borderRadius="15px"
      mb={2}
      bgcolor="white"
      boxShadow={2}
      borderColor="white"
      flexWrap={{ xs: "wrap", md: "nowrap" }}
      mx={"auto"}
      overflow={"hidden"}
      // width={{ xs: "293px", md: "1105px" }}
      width={{ xs: "100%", lg: "100%", md: "100%", xl: "100%" }}
    >

      {myLearningData.map((item: any, index: number) => (
        <>
          <Box
            key={index}
            display="flex"
            gap={"10px"}
            alignItems="center"
            flexWrap={{ xs: "wrap", md: "nowrap" }}
          >
            <CardMedia
              component="img"
              sx={styleSheet.img}
              image={item?.contentData?.coverPhotoUrl}
              alt={item?.contentData?.title}
            />
            <Box display="flex" flexDirection="column" gap={"4px"}>
              <Typography
                sx={{ width: { xs: "293px", md: "337px" } }}
                fontSize={"18px"}
                fontWeight={700}
                color={"#233F91"}
              >
                {item?.contentData?.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <img src="assets/icons/affcategory.png" alt={item.category} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  component="div"
                  sx={{ fontWeight: 400, fontSize: "17px", }}
                >
                  {/* {item?.contentData?.category} */}
                  {Array.isArray(item?.contentData?.category)
                    ? item?.contentData?.category.join(', ')
                    : item?.contentData?.category.replace(/\//g, ' / ')}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  alt={""}
                  src={item?.contentData?.createdBy?.profileUrl}
                  sx={{ width: { xs: 25, md: 27 }, height: { xs: 25, md: 27 } }}
                />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  component="div"
                  sx={{ fontWeight: 400, fontSize: "17px" }}
                >
                  {`${item?.contentData?.createdBy?.firstName || ""} ${item?.contentData?.createdBy?.lastName || ""
                    }`.trim()}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <img src="assets/icons/affcourse.png" alt={item.type} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  component="div"
                  sx={{ fontWeight: 400, fontSize: "17px" }}
                >
                  {item?.contentData?.type}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            position={"relative"}
            justifyContent={{ xs: "center", md: "space-between" }}
            flexWrap={{ xs: "wrap", md: "nowrap" }}
            gap={
              item.type === "Handout" ? { xs: 5, md: 12 } : { xs: 5, md: 12 }
            }
            pt={{ xs: 2, md: 0 }}

          >
            {showProgressBar && (
              <Box display="flex" flexDirection="column" gap={2}
                mr={10}
              >
                {showLastAccessed && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      fontWeight: 400,
                      fontSize: "17px",
                    }}
                  >

                    {/* Last Accessed: {dayjs(item?.createdAt?.toDate()).format("MM/DD/YYYY")} */}

                    Last Accessed:{" "}
                    {item.learningLogs && Array.isArray(item.learningLogs) && item.learningLogs.length > 0

                      ? item.learningLogs
                        .map((log: any) =>
                          log.createdAt
                            ? dayjs(log.createdAt.toDate()).format("MM/DD/YYYY")
                            : "Invalid Date"
                        )
                        .join(", ") // ✅ Show multiple dates with commas
                      : `${dayjs(item?.createdAt?.toDate()).format("MM/DD/YYYY")}`}


                  </Typography>
                )}
                <Box>
                  <Box
                    width={214}
                    height={8}
                    mb={1}
                    bgcolor="#E0E0E0"
                    borderRadius={4}
                    position="relative"
                  >
                    <Box
                      width={`${progress}%`}
                      bgcolor="green"
                      height={8}
                      borderRadius={4}
                      position="absolute"
                    />
                  </Box>
                  <Typography ml={14} variant="body2" color="textSecondary">
                    {`${progress}% Completed`}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box position={"relative"} display={"flex"} gap={2} mr={1}>
              {item?.contentData?.type === "handout" && (
                <Button
                  sx={styleSheet.dowButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => onDownloadClick(item)} // ✅ Pass specific cartItem
                >
                  Download
                </Button>
              )}
              <Button
                sx={{
                  ...styleSheet.rowButton,
                  width: progress > 0 ? "96.3px" : "112px",
                }}
                variant="contained"
                color="primary"
                onClick={
                  () =>
                    progress > 0
                      ? onContinueClick(item) // Pass the correct cartItem
                      : onStartClick(item) // Pass the correct cartItem
                }
              >
                {progress > 0 ? "Continue" : "Start"}
              </Button>

            </Box>
          </Box >
        </>
      ))
      }
    </Box >
  );
};
export default LearningCard;

const styleSheet = {
  rowButton: {
    borderRadius: "15px",
    width: "112px",
    height: "48px",
    // marginRight: "10px",
    textTransform: "none",
    backgroundColor: "#233f91",
    color: "#ffffff",
    Padding: "8px, 25px, 8px, 25px",
    fontSize: "16px",
    gap: "10px",
    fontWeight: 700,
  },
  dowButton: {
    position: "absolute",
    right: "120px",
    top: 0,
    borderRadius: "15px",
    width: "112px",
    height: "48px",
    textTransform: "none",
    color: "#233F91",
    border: "1px solid #233F91",
    fontSize: "18px",
    gap: "10px",
    fontWeight: 700,
  },
  img: {
    width: 192,
    height: 192,
    borderRadius: "10px",
    objectFit: "cover",
    cursor: "pointer",
    mx: "auto",
  },
};
